import { ExternalLink } from 'lucide-react'
import type { Product } from '../../types'
import { formatPrice } from '../../lib/utils'
import StockBadge from './StockBadge'
import { Button } from '../ui/button'

interface ProductDistributorOffersProps {
  product: Product
}

export default function ProductDistributorOffers({ product }: ProductDistributorOffersProps) {
  const hasRS = product.product_url_rs || product.rs_stock_number
  const hasDistrelec = product.product_url_distrelec || product.distrelec_stock_number

  if (!hasRS && !hasDistrelec) {
    return (
      <div className="py-8 text-center">
        <p className="text-neutral-600 font-medium mb-2">Contact us for pricing</p>
        <p className="text-sm text-neutral-400">
          This product is available through Global Control Group. Please contact us for current pricing and availability.
        </p>
        <div className="mt-4">
          <Button variant="primary" asChild>
            <a href="mailto:info@globalcontrolgroup.nl?subject=Price request">
              Request pricing
            </a>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* RS Online */}
      {hasRS && (
        <div className="rounded-lg border border-neutral-200 p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-sm text-neutral-900">RS Online</p>
              {product.rs_stock_number && (
                <p className="text-xs text-neutral-400">Stock #: {product.rs_stock_number}</p>
              )}
            </div>
            <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded font-medium">RS</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              {product.price_ex_vat != null ? (
                <p className="text-lg font-bold text-neutral-900">
                  {formatPrice(product.price_ex_vat, product.price_currency)}
                </p>
              ) : (
                <p className="text-sm text-neutral-500 italic">Price on request</p>
              )}
              <p className="text-xs text-neutral-400">ex. VAT</p>
            </div>
            <StockBadge status={product.stock_status} />
          </div>

          {product.moq > 1 && (
            <p className="text-xs text-neutral-400">MOQ: {product.moq} pcs</p>
          )}
          {product.lead_time_days != null && (
            <p className="text-xs text-neutral-400">Lead time: {product.lead_time_days} days</p>
          )}

          {product.product_url_rs && (
            <Button variant="secondary" size="sm" asChild className="mt-auto">
              <a href={product.product_url_rs} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
                Buy at RS Online
              </a>
            </Button>
          )}
        </div>
      )}

      {/* Distrelec */}
      {hasDistrelec && (
        <div className="rounded-lg border border-neutral-200 p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-sm text-neutral-900">Distrelec</p>
              {product.distrelec_stock_number && (
                <p className="text-xs text-neutral-400">Stock #: {product.distrelec_stock_number}</p>
              )}
            </div>
            <span className="text-xs bg-gcg-tint text-primary px-2 py-0.5 rounded font-medium">Distrelec</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500 italic">Price on request</p>
              <p className="text-xs text-neutral-400">Contact for pricing</p>
            </div>
            <StockBadge status="unknown" />
          </div>

          {product.product_url_distrelec && (
            <Button variant="secondary" size="sm" asChild className="mt-auto">
              <a href={product.product_url_distrelec} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
                Buy at Distrelec
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
